/**
 * Closed catalog Select with optional pinned «Thêm …» footer.
 * Options and create dialog stay in the module.
 */
import React, { type Ref } from 'react';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectPlaceholder,
} from './FormControls';
import { useQuickCreateMenuPaper } from './useQuickCreateMenuPaper';

const noop = (): void => undefined;

const selectIconSx = {
  '&:not(:hover):not(:focus-within) .MuiSelect-icon': { display: 'none' },
  '&:not(:hover):not(:focus-within) .MuiSelect-select': { pr: '8px !important' },
} as const;

export interface CatalogSelectOption {
  value: string;
  label: string;
}

export interface CatalogSelectQuickCreate {
  canCreate: boolean;
  createLabel: string;
  onQuickCreate: () => void;
}

export interface CatalogSelectFieldProps {
  value: string;
  onChange: (value: string) => void;
  options: CatalogSelectOption[];
  label: string;
  placeholder: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  name?: string;
  onBlur?: () => void;
  inputRef?: Ref<unknown>;
  renderValue?: (value: string) => React.ReactNode;
  quickCreate?: CatalogSelectQuickCreate;
}

const CatalogSelectField: React.FC<CatalogSelectFieldProps> = ({
  value,
  onChange,
  options,
  label,
  placeholder,
  required = false,
  disabled = false,
  error,
  name,
  onBlur,
  inputRef,
  renderValue,
  quickCreate,
}) => {
  const {
    menuOpen,
    onMenuOpen,
    onMenuClose,
    selectMenuPaperSlotProps,
  } = useQuickCreateMenuPaper({
    canCreate: Boolean(quickCreate?.canCreate),
    createLabel: quickCreate?.createLabel ?? '',
    onQuickCreate: quickCreate?.onQuickCreate ?? noop,
  });

  const selectedLabel = (selected: string) => {
    if (renderValue) return renderValue(selected);
    return options.find((option) => option.value === selected)?.label ?? selected;
  };

  return (
    <FormControl
      fullWidth
      required={required}
      error={Boolean(error)}
      disabled={disabled}
      sx={selectIconSx}
    >
      <InputLabel shrink>{label}</InputLabel>
      <Select
        label={label}
        name={name}
        value={value}
        open={quickCreate ? menuOpen : undefined}
        onOpen={quickCreate ? onMenuOpen : undefined}
        onClose={quickCreate ? onMenuClose : undefined}
        displayEmpty
        notched
        onChange={(event) => onChange(String(event.target.value))}
        onBlur={onBlur}
        inputRef={inputRef}
        MenuProps={
          quickCreate
            ? { slotProps: { paper: selectMenuPaperSlotProps } }
            : undefined
        }
        renderValue={(selected) =>
          selected ? (
            selectedLabel(String(selected))
          ) : (
            <SelectPlaceholder>{placeholder}</SelectPlaceholder>
          )
        }
      >
        <MenuItem value="">{placeholder}</MenuItem>
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {error ? <FormHelperText>{error}</FormHelperText> : null}
    </FormControl>
  );
};

export default CatalogSelectField;
